import { string, bool, func, PropTypes } from 'prop-types';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import usePreviouslyAssigned from '../../data/queryHooks/shared/physicalTherapist/usePreviouslyAssigned';
import VPTRXCard from '../../components/shared/components/VPTRXCard/VPTRXCard';
import { exerciseDetailRoutes } from '../../scripts/hrefs';
import emptyCards from '../../components/shared/assets/emptyCards.webp';
import programTypes, { programNames } from '../../scripts/constants';
import VirtualMSKResourceMemberCard from '../../components/shared/components/VirtualMSKResourceMemberCard/VirtualMSKResourceMemberCard';

const PreviouslyAssignedContent = ({
	apiEnv,
	ssId,
	apiToken,
	pathToActionPlanRoutes,
	isAdmin,
	memberId,
	programType,
	navigateToResourceLibrary,
}) => {
	const memberProgramDetailHistory = usePreviouslyAssigned(
		apiEnv,
		ssId,
		programNames(programType),
		isAdmin,
		apiToken,
		memberId
	);

	const navigate = useNavigate();

	// define and memoize the card click callback
	const handleVPTRXCardClick = useCallback(
		id => {
			navigate(
				`${pathToActionPlanRoutes}${exerciseDetailRoutes(programType)}/${id}` // TODO: fix the path here for customized
			);
		},
		[navigate, pathToActionPlanRoutes, programType]
	);

	const exerciseItems = memberProgramDetailHistory
		.filter(
			memberActionItem =>
				//This first condition is because only MSK data has the isExercise flag and we need to load exercise items for other programs
				programType !== programTypes.virtualMSK ||
				memberActionItem.isExercise === true
		)
		.map(memberActionItem => {
			const { id, reps, sets, selectedRating, resource, frequency } =
				memberActionItem;
			return (
				<Box
					gridColumn={{
						xs: 'span 12',
						sm: 'span 6',
						lg: 'span 4',
						xl: 'span 3',
					}}
					key={id}
				>
					<VPTRXCard
						reps={reps}
						sets={sets}
						frequency={frequency}
						selectedRating={selectedRating}
						resource={resource}
						apiEnv={apiEnv}
						ssId={ssId}
						id={id}
						onCardClick={{
							disabled: !!isAdmin,
							handleCardClick: () => {
								handleVPTRXCardClick(id);
							},
						}}
					/>
				</Box>
			);
		});

	const mskResourceItems = memberProgramDetailHistory
		.filter(memberActionItem => memberActionItem.isExercise === false)
		.map((memberActionItem, index) => {
			const { id, resource } = memberActionItem;

			return (
				<Box
					gridColumn={{
						xs: 'span 12',
						sm: 'span 6',
						lg: 'span 4',
						xl: 'span 3',
					}}
					key={id}
					data-testid="widget-action-item"
				>
					<VirtualMSKResourceMemberCard
						key={id}
						resource={resource}
						isEditing
						index={index}
						navigateToResourceLibrary={navigateToResourceLibrary}
					/>
				</Box>
			);
		});

	// If there are no previously assigned items, display a special view.
	if (!memberProgramDetailHistory || memberProgramDetailHistory.length === 0) {
		return (
			<Stack textAlign="center" mt={16}>
				<Stack justifyContent="center" direction="row" mb={2}>
					<img
						width="200px"
						mx="auto"
						src={emptyCards}
						alt="Cards spread out showing no exercsies assigned yet."
					/>
				</Stack>
				<Typography
					variant="subtitle1"
					component="h2"
					data-testid="no-previously-assigned-text"
				>
					No previously assigned items yet.
				</Typography>
				<Typography
					variant="body1"
					component="h3"
					color="text.secondary"
					data-testid="no-previously-assigned-text-secondary"
				>
					{programType === programTypes.virtualMSK
						? 'Previously assigned items will be shown here.'
						: 'Items that your care team member previously assigned will be shown here.'}
				</Typography>
			</Stack>
		);
	}

	// Show the previously assigned items in a grid
	return (
		<Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={4} m="auto">
			{exerciseItems}
			{mskResourceItems}
		</Box>
	);
};

PreviouslyAssignedContent.propTypes = {
	/*
	 * The environment the component is being hosted on such as "prod" or "dev"
	 */
	apiEnv: string.isRequired,
	/*
	 * Session ID to be added to the header when calling the API.
	 */
	ssId: string,
	/*
	 * API token to be added to the header when calling the API.
	 */
	apiToken: string,
	/*
	 * pathToActionPlanRoutes: string that leads to where action plan routes are mounted. Eg. if you mounted actionPlanRoutes at `/actionPlan`, you would pass "/actionPlan"
	 */
	pathToActionPlanRoutes: string,
	/*
	 * Member ID to be added to the header when calling the API.
	 */
	memberId: string,
	/*
	 * Flag to indicate to the component if the consumer is an admin site
	 */
	isAdmin: bool,
	/*
	 * defines which program content we need to display for previously assigned
	 */
	programType: PropTypes.oneOf([
		programTypes.occupationalTherapy,
		programTypes.physicalTherapy,
		programTypes.virtualMSK,
	]),
	/*
	 * Consumer defined callback to navigate to the resource library
	 */
	navigateToResourceLibrary: func,
};

PreviouslyAssignedContent.defaultProps = {
	pathToActionPlanRoutes: '',
	memberId: '',
	isAdmin: false,
	programType: programTypes.physicalTherapy,
	ssId: '',
	apiToken: '',
	navigateToResourceLibrary: () => {},
};

export default PreviouslyAssignedContent;
